import { html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import type AtlasImage from "@/components/display/atlas-image/atlas-image";
import type AtlasImagePreviewControl from "@/components/display/atlas-image-preview-control/atlas-image-preview-control";
import type AtlasPreview from "@/components/display/atlas-preview/atlas-preview";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";

import type { ImagePreviewAction } from "./types";

import styles from "./atlas-image-preview.scss";

import "@/components/display/atlas-icon/atlas-icon";
import "@/components/display/atlas-image-preview-control/atlas-image-preview-control";

export type ImagePreviewProps = AtlasElementProps & {
    "open": boolean;
    "prevent-close": boolean;
    "prevent-open-preview-on-click": boolean;
    "actions": Array<ImagePreviewAction>;
};

/**
 * @dependency atlas-icon
 * @dependency atlas-image-preview-control
 *
 * @tag atlas-image-preview
 */
@customElement("atlas-image-preview")
export default class AtlasImagePreview extends AtlasElement {
    static styles = styles;

    /** Indica se o preview está aberto */
    @property({ type: Boolean, reflect: true }) open = false;

    /** Previne o fechamento se clicado fora do preview */
    @property({ type: Boolean, reflect: true, attribute: "prevent-close" }) preventClose = false;

    /** Previne a abertura do preview quando clicado no elemento de imagem */
    @property({ type: Boolean, reflect: true, attribute: "prevent-open-preview-on-click" }) preventOpenPreviewOnClick =
        false;

    /** Quais ações para manipular a imagem estarão ativas, podendo conter: flip, rotate, redefine, zoomIn, zoomOut e download */
    @property({ type: Array }) actions: Array<ImagePreviewAction> = [];

    @state() private _slottedImageElements: (AtlasImage | AtlasPreview)[];

    @state() private _imagePreviewItems: HTMLElement[];

    @state() private _activeImage: HTMLElement | null = null;

    @state() private _activeImageIndex = 0;

    private _isChangingActiveImage = false;

    private _isDragging = false;

    private _isZooming = false;

    private _currentTranslation: { x: number; y: number } = { x: 0, y: 0 };

    private _zoomTouchStartDistance: number;

    private _zoomLimit = {
        min: 0.4,
        max: 4
    };

    @query(".atlas-image-preview-content")
    private _imagePreviewContainer: HTMLDivElement;

    @query(".atlas-image-preview-prev")
    private _imagePreviewPrev: AtlasImagePreviewControl;

    @query(".atlas-image-preview-next")
    private _imagePreviewNext: AtlasImagePreviewControl;

    constructor() {
        super();

        this.zoomWithWheel = this.zoomWithWheel.bind(this);
        this.zoomWithTouch = this.zoomWithTouch.bind(this);
        this.dragStart = this.dragStart.bind(this);
        this.drag = this.drag.bind(this);
        this.dragEnd = this.dragEnd.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.touchStart = this.touchStart.bind(this);
        this.touchMove = this.touchMove.bind(this);
        this.touchEnd = this.touchEnd.bind(this);
        this.imageClick = this.imageClick.bind(this);
    }

    private onSlotChange() {
        this.getSlottedImageElements();
        this.setImagesInPreview();
        this.getImagesInPreview();
        this.setClickEventOnImageElements();
    }

    private getSlottedImageElements() {
        const slottedElements = this.querySelectorAll("atlas-image, atlas-preview");

        if (!slottedElements) return;

        this._slottedImageElements = Array.from(slottedElements) as (AtlasImage | AtlasPreview)[];
    }

    private setImagesInPreview() {
        this._imagePreviewContainer.innerHTML = "";

        this._slottedImageElements.forEach((imageElement, index) => {
            imageElement.setAttribute("data-preview-image-index", index.toString());

            const imagePreviewElement = this.createImagePreviewElement(imageElement, index);

            this._imagePreviewContainer.appendChild(imagePreviewElement);
        });
    }

    private getImagesInPreview() {
        this._imagePreviewItems = Array.from(this._imagePreviewContainer.querySelectorAll(".atlas-image-item"));
    }

    private createImagePreviewElement(imageElement: AtlasImage | AtlasPreview, index: number) {
        const imagePreviewTemplate = this.shadowRoot.querySelector("template").content.cloneNode(true) as HTMLElement;
        imagePreviewTemplate
            .querySelector(".atlas-image-item")
            .setAttribute("data-preview-image-index", index.toString());

        const imagePreview = imagePreviewTemplate.querySelector(".atlas-image");

        const src = imageElement.getAttribute("src") || imageElement.getAttribute("image");
        const alt = imageElement.getAttribute("alt") || imageElement.getAttribute("image-name");

        imagePreview.setAttribute("src", src);
        imagePreview.setAttribute("alt", alt);

        if (imageElement.hasAttribute("data-name") || imageElement.getAttribute("image-name")) {
            const imageName = imageElement.getAttribute("data-name") || imageElement.getAttribute("image-name");
            imagePreview.setAttribute("data-name", imageName);
        }

        if (imageElement.hasAttribute("data-download-url")) {
            imagePreview.setAttribute("data-download-url", imageElement.getAttribute("download-url"));
        }

        imagePreview.addEventListener("wheel", this.zoomWithWheel);
        imagePreview.addEventListener("mousedown", this.dragStart);
        imagePreview.addEventListener("touchstart", this.touchStart);
        imagePreview.addEventListener("click", this.imageClick);

        return imagePreviewTemplate;
    }

    private setClickEventOnImageElements() {
        if (this.preventOpenPreviewOnClick) return;

        this._slottedImageElements.forEach((imageElement) => {
            imageElement.addEventListener("click", () => {
                const clickedImageIndex = this.getPreviewImageIndex(imageElement);
                this.openPreview(clickedImageIndex);
            });
        });
    }

    private resetImagesStates() {
        this._imagePreviewItems.forEach((imageItem) => {
            imageItem.classList.remove("active");
            imageItem.classList.remove("prev");
            imageItem.classList.remove("next");
        });

        this._activeImage = null;
        this._activeImageIndex = 0;
    }

    private openPreview(activeImageIndex: number) {
        this.open = true;
        this.setActiveImage(activeImageIndex);

        document.body.classList.toggle("disable-scroll", true);
        document.addEventListener("keydown", this.onKeyDown);
    }

    private closePreview() {
        if (this._isDragging) return;

        this.open = false;
        this.resetImagesStates();

        document.body.classList.toggle("disable-scroll", false);
        document.removeEventListener("keydown", this.onKeyDown);
    }

    private imageClick(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
    }

    private getPreviewImageIndex(imageItem: HTMLElement) {
        return parseInt(imageItem.getAttribute("data-preview-image-index"), 10);
    }

    private indexOutOfBounds(index: number) {
        return index < 0 || index >= this._imagePreviewItems.length;
    }

    private setActiveImage(index: number) {
        if (this.indexOutOfBounds(index)) return;

        if (this._isChangingActiveImage) return;

        this._isChangingActiveImage = true;

        const lastActiveImage = this._activeImage;

        this._activeImageIndex = index;
        this._activeImage = this._imagePreviewItems.find((imageItem) => this.getPreviewImageIndex(imageItem) === index);

        this.defineNextAndPrevImages();

        this._activeImage.classList.add("active");

        this.setImageTitle();

        setTimeout(() => {
            lastActiveImage?.classList.remove("active");
            this._isChangingActiveImage = false;
        }, 1000);
    }

    private setImageTitle() {
        const imageTitle = this._activeImage.querySelector(".atlas-image").getAttribute("data-name");
        const imagePreviewHeader = this.shadowRoot.querySelector(".atlas-image-header");

        imagePreviewHeader.innerHTML = imageTitle || "";
    }

    private resetNextAndPrevImages() {
        this._imagePreviewItems.forEach((imageItem) => {
            imageItem.classList.remove("prev");
            imageItem.classList.remove("next");
        });
    }

    private defineNextAndPrevImages() {
        this.resetNextAndPrevImages();

        const step = 1;

        const previousImageIndex = this._activeImageIndex - step;
        const nextImageIndex = this._activeImageIndex + step;

        this.setPrevImage(previousImageIndex);
        this.setNextImage(nextImageIndex);
    }

    private setPrevImage(previousImageIndex: number) {
        const hasPreviousImage = previousImageIndex >= 0;

        if (hasPreviousImage) {
            this._imagePreviewItems[previousImageIndex].classList.add("prev");
        }

        this._imagePreviewPrev.disabled = !hasPreviousImage;
    }

    private setNextImage(nextImageIndex: number) {
        const hasNextImage = nextImageIndex < this._imagePreviewItems.length;

        if (hasNextImage) {
            this._imagePreviewItems[nextImageIndex].classList.add("next");
        }

        this._imagePreviewNext.disabled = !hasNextImage;
    }

    private goToPreviousImage(event: Event) {
        event.stopPropagation();
        event.preventDefault();

        const previousImageIndex = this._activeImageIndex - 1;

        this.setActiveImage(previousImageIndex);
    }

    private goToNextImage(event: Event) {
        event.stopPropagation();
        event.preventDefault();

        const nextImageIndex = this._activeImageIndex + 1;

        this.setActiveImage(nextImageIndex);
    }

    private onKeyDown(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.closePreview();
        }

        if (event.key === "ArrowLeft") {
            this.goToPreviousImage(event);
        }

        if (event.key === "ArrowRight") {
            this.goToNextImage(event);
        }

        if (event.ctrlKey && event.key === "=") {
            this.zoomIn(event);
        }

        if (event.ctrlKey && event.key === "-") {
            this.zoomOut(event);
        }

        if (event.ctrlKey && event.key === "1") {
            this.resetZoom(event);
        }
    }

    private dragStart(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();

        this._isDragging = true;
        this.setCurrentTranslation(event.clientX, event.clientY);

        this._activeImage.addEventListener("mousemove", this.drag);
        this._activeImage.addEventListener("mouseup", this.dragEnd);
    }

    private drag(event: MouseEvent) {
        if (!this._isDragging) return;

        event.stopPropagation();
        event.preventDefault();

        this.updateTranslation(event.clientX, event.clientY);
    }

    private dragEnd(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();

        this._isDragging = false;

        this._activeImage.removeEventListener("mousemove", this.drag);
        this._activeImage.removeEventListener("mouseup", this.dragEnd);
    }

    private touchStart(event: TouchEvent) {
        event.stopPropagation();
        event.preventDefault();

        if (event.targetTouches.length == 2) {
            const x = event.targetTouches[0].clientX - event.targetTouches[1].clientX;
            const y = event.targetTouches[0].clientY - event.targetTouches[1].clientY;

            this._isZooming = true;
            this.setZoomTouchStartDistance(Math.hypot(x, y));
        } else if (event.targetTouches.length == 1) {
            const x = event.targetTouches[0].clientX;
            const y = event.targetTouches[0].clientY;

            this._isDragging = true;
            this.setCurrentTranslation(x, y);
        }

        this._activeImage.addEventListener("touchmove", this.touchMove);
        this._activeImage.addEventListener("touchend", this.touchEnd);
        this._activeImage.addEventListener("touchcancel", this.touchEnd);
    }

    private touchMove(event: TouchEvent) {
        event.stopPropagation();
        event.preventDefault();

        if (this._isZooming) this.zoomWithTouch(event);

        if (this._isDragging) this.updateTranslation(event.touches[0].clientX, event.touches[0].clientY);
    }

    private touchEnd(event: TouchEvent) {
        event.stopPropagation();
        event.preventDefault();

        this._isDragging = false;
        this._isZooming = false;

        this._activeImage.removeEventListener("touchmove", this.touchMove);
        this._activeImage.removeEventListener("touchend", this.touchEnd);
        this._activeImage.removeEventListener("touchcancel", this.touchEnd);
    }

    private setZoomTouchStartDistance(distance: number) {
        this._zoomTouchStartDistance = distance;
    }

    private setCurrentTranslation(posX: number, posY: number) {
        this._currentTranslation = {
            x: posX,
            y: posY
        };
    }

    private updateTranslation(posX: number, posY: number) {
        const translatingElement = this._activeImage as HTMLDivElement;

        const newX = this._currentTranslation.x - posX;
        const newY = this._currentTranslation.y - posY;

        this.setCurrentTranslation(posX, posY);

        translatingElement.style.top = translatingElement.offsetTop - newY + "px";
        translatingElement.style.left = translatingElement.offsetLeft - newX + "px";
    }

    private resetTranslation() {
        const translatingElement = this._activeImage as HTMLDivElement;

        translatingElement.style.top = "0px";
        translatingElement.style.left = "0px";

        this.setCurrentTranslation(0, 0);
    }

    private getActiveImageScale(element: HTMLElement) {
        const scale = element.style.transform.trim();

        if (!scale)
            return {
                x: 1,
                y: 1,
                z: 1
            };

        const [x, y, z] = scale
            .match(/scale3d\(([^)]+)\)/)[1]
            .split(",")
            .map(parseFloat);

        return { x, y, z };
    }

    private getActiveImageRotation(element: HTMLElement) {
        const rotation = element.style.transform.trim();

        if (!rotation) return 0;

        return parseInt(rotation.match(/rotate\(([^)]+)\)/)[1], 10);
    }

    private flipImage(event: Event) {
        if (!this.actions.includes("flip")) return;

        event.stopPropagation();
        event.preventDefault();

        const flipElement = this._activeImage.querySelector(".atlas-image-flip") as HTMLDivElement;
        const scale = this.getActiveImageScale(flipElement);

        flipElement.style.transform = `scale3d(${scale.x * -1},${scale.y},${scale.z})`;
    }

    private rotateImage(event: Event) {
        if (!this.actions.includes("rotate")) return;

        event.stopPropagation();
        event.preventDefault();

        const rotationElement = this._activeImage.querySelector(".atlas-image-rotate") as HTMLDivElement;

        const rotation = this.getActiveImageRotation(rotationElement);

        const flipElement = this._activeImage.querySelector(".atlas-image-flip") as HTMLDivElement;
        const isFlipped = this.getActiveImageScale(flipElement).x < 0;
        const rotatingDegree = isFlipped ? -90 : 90;

        const newRotation = rotation + rotatingDegree;

        rotationElement.style.transform = `rotate(${newRotation}deg)`;
    }

    private zoomWithTouch(event: TouchEvent) {
        event.stopPropagation();
        event.preventDefault();

        if (event.targetTouches.length !== 2 || event.changedTouches.length !== 2) return;

        const newXTouchDistance = event.changedTouches[0].clientX - event.changedTouches[1].clientX;
        const newYTouchDistance = event.changedTouches[0].clientY - event.changedTouches[1].clientY;

        const newTouchDistance = Math.hypot(newXTouchDistance, newYTouchDistance);

        if (this._zoomTouchStartDistance === newTouchDistance) return;

        const direction = this._zoomTouchStartDistance > newTouchDistance ? "out" : "in";

        this.setZoomTouchStartDistance(newTouchDistance);

        this.zoom(0.01, direction);
    }

    private zoomWithWheel(event: WheelEvent) {
        event.stopPropagation();
        event.preventDefault();

        const direction = event.deltaY < 0 ? "in" : "out";

        this.zoom(0.1, direction);
    }

    private zoomIn(event: Event) {
        event.stopPropagation();
        event.preventDefault();

        this.zoom(0.1, "in");
    }

    private zoomOut(event: Event) {
        event.stopPropagation();
        event.preventDefault();

        this.zoom(0.1, "out");
    }

    private zoom(sensibility: number, direction: "in" | "out") {
        const zoomElement = this._activeImage.querySelector(".atlas-image") as HTMLDivElement;
        const scale = this.getActiveImageScale(zoomElement);

        if (direction === "in" && !this.canZoomIn(scale)) return;
        if (direction === "out" && !this.canZoomOut(scale)) return;

        const zoomFactor = direction === "in" ? 1 : -1;

        const newX = scale.x + zoomFactor * sensibility;
        const newY = scale.y + zoomFactor * sensibility;
        const newZ = scale.z;

        zoomElement.style.transform = `scale3d(${newX},${newY},${newZ})`;
    }

    private resetZoom(event: Event) {
        if (!this.actions.includes("redefineZoom")) return;

        event.stopPropagation();
        event.preventDefault();

        const zoomElement = this._activeImage.querySelector(".atlas-image") as HTMLDivElement;

        zoomElement.style.transform = `scale3d(1,1,1)`;
        this.resetTranslation();
    }

    private canZoomIn(scale: { x: number; y: number; z: number }) {
        return this.actions.includes("zoomIn") && scale.x < this._zoomLimit.max;
    }

    private canZoomOut(scale: { x: number; y: number; z: number }) {
        return this.actions.includes("zoomOut") && scale.x > this._zoomLimit.min;
    }

    private downloadImage(event: MouseEvent) {
        if (!this.actions.includes("download")) return;

        event.stopPropagation();
        event.preventDefault();

        const imageElement = this._activeImage.querySelector(".atlas-image") as HTMLImageElement;

        const link = document.createElement("a");
        link.href = imageElement.getAttribute("data-download-url") || imageElement.getAttribute("src");
        link.download = imageElement.getAttribute("data-name") || "image.jpg";
        link.target = "_blank";
        link.click();
    }

    private renderCloseButton() {
        return html`
            <atlas-image-preview-control
                tooltip="Fechar"
                tooltip-placement="top"
                icon="x"
                class="atlas-image-preview-close"
                @atlas-image-preview-control-click=${this.closePreview}
            ></atlas-image-preview-control>
        `;
    }

    private renderPrevButton() {
        return html`
            <atlas-image-preview-control
                tooltip="Anterior"
                tooltip-placement="top"
                icon="chevron-left"
                class="atlas-image-preview-prev"
                @atlas-image-preview-control-click=${this.goToPreviousImage}
            ></atlas-image-preview-control>
        `;
    }

    private renderNextButton() {
        return html`
            <atlas-image-preview-control
                tooltip="Próximo"
                tooltip-placement="top"
                icon="chevron-right"
                class="atlas-image-preview-next"
                @atlas-image-preview-control-click=${this.goToNextImage}
            ></atlas-image-preview-control>
        `;
    }

    private renderFlipControl() {
        return when(
            this.actions.includes("flip"),
            () => html`
                <atlas-image-preview-control
                    tooltip="Espelhar"
                    tooltip-placement="top"
                    icon="arrow-right-left"
                    @atlas-image-preview-control-click=${this.flipImage}
                ></atlas-image-preview-control>
            `
        );
    }

    private renderRotateControl() {
        return when(
            this.actions.includes("rotate"),
            () => html`
                <atlas-image-preview-control
                    tooltip="Girar para a direita"
                    tooltip-placement="top"
                    icon="rotate"
                    @atlas-image-preview-control-click=${this.rotateImage}
                ></atlas-image-preview-control>
            `
        );
    }

    private renderRedefineZoomControl() {
        return when(
            this.actions.includes("redefineZoom"),
            () => html`
                <atlas-image-preview-control
                    tooltip="Redefinir zoom"
                    tooltip-placement="top"
                    icon="maximize"
                    @atlas-image-preview-control-click=${this.resetZoom}
                ></atlas-image-preview-control>
            `
        );
    }

    private renderZoomOutControl() {
        return when(
            this.actions.includes("zoomOut"),
            () => html`
                <atlas-image-preview-control
                    tooltip="Diminuir zoom"
                    tooltip-placement="top"
                    icon="minus-circle"
                    @atlas-image-preview-control-click=${this.zoomOut}
                ></atlas-image-preview-control>
            `
        );
    }

    private renderZoomInControl() {
        return when(
            this.actions.includes("zoomIn"),
            () => html`
                <atlas-image-preview-control
                    tooltip="Aumentar zoom"
                    tooltip-placement="top"
                    icon="plus-circle"
                    @atlas-image-preview-control-click=${this.zoomIn}
                ></atlas-image-preview-control>
            `
        );
    }

    private renderZoomControls() {
        return html` ${this.renderRedefineZoomControl()} ${this.renderZoomOutControl()} ${this.renderZoomInControl()} `;
    }

    private renderDownloadControl() {
        return when(
            this.actions.includes("download"),
            () => html`
                <atlas-image-preview-control
                    tooltip="Baixar"
                    tooltip-placement="top"
                    icon="download"
                    @atlas-image-preview-control-click=${this.downloadImage}
                ></atlas-image-preview-control>
            `
        );
    }

    private renderImageControls() {
        return html`
            <div class="atlas-image-preview-controls">
                ${this.renderFlipControl()} ${this.renderRotateControl()} ${this.renderZoomControls()}
                ${this.renderDownloadControl()}
            </div>
        `;
    }

    private renderCounter() {
        if (!this._imagePreviewItems || this._imagePreviewItems.length === 1) return nothing;

        return html`
            <div class="atlas-image-preview-counter">
                ${this._activeImageIndex + 1} de ${this._imagePreviewItems.length}
            </div>
        `;
    }

    private renderImageItemTemplate() {
        return html`
            <template>
                <div class="atlas-image-item">
                    <picture class="atlas-image-wrapper">
                        <div class="atlas-image-flip">
                            <div class="atlas-image-rotate">
                                <img class="atlas-image" />
                            </div>
                        </div>
                    </picture>
                </div>
            </template>
        `;
    }

    private onBackdropClick(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();

        if (this.preventClose) return;

        this.closePreview();
    }

    /** @internal */
    public render() {
        const atlasImagePreviewWrapperClass = {
            "atlas-image-preview-wrapper": true,
            "open": this.open
        };

        const atlasImagePreviewClass = {
            "atlas-image-preview": true,
            "open": this.open
        };

        const atlasImagePreviewBackdropClass = {
            "atlas-image-preview-backdrop": true,
            "open": this.open
        };

        return html`
            <slot @slotchange=${this.onSlotChange}></slot>
            <div class="${classMap(atlasImagePreviewWrapperClass)}" @click=${this.onBackdropClick}>
                <div class="${classMap(atlasImagePreviewBackdropClass)}"></div>
                <div class="${classMap(atlasImagePreviewClass)}">
                    <div class="atlas-image-header"></div>
                    ${this.renderCloseButton()} ${this.renderPrevButton()} ${this.renderNextButton()}
                    ${this.renderImageControls()} ${this.renderCounter()}
                    <div class="atlas-image-preview-content"></div>
                </div>
            </div>
            ${this.renderImageItemTemplate()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-image-preview": AtlasImagePreview;
    }
}
